// src/components/ChunkErrorBoundary.tsx
//
// Top-level React error boundary that catches two classes of errors:
//
// 1. Chunk load errors ("Failed to fetch dynamically imported module")
//    → auto-reload once to pick up the fresh index.html after a deploy.
//
// 2. All other runtime errors (e.g. React Router hooks mismatch, render
//    exceptions, etc.) → render a manual "Reload" fallback instead of
//    leaving the user with a blank screen.
//
// IMPORTANT: getDerivedStateFromError must NOT re-throw errors. Throwing
// inside that lifecycle causes React to unmount the entire tree (blank
// screen with no UI at all). Always return state and handle in render().

import { Component, type ReactNode, type ErrorInfo } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  reloading: boolean;
  errorMessage: string;
  isChunkError: boolean;
}

const RELOAD_KEY = "chunk_error_reload_attempted";

function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message ?? "";
  return (
    msg.includes("Failed to fetch dynamically imported module") ||
    msg.includes("Importing a module script failed") ||
    msg.includes("error loading dynamically imported module") ||
    msg.includes("Unable to preload CSS")
  );
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, reloading: false, errorMessage: "", isChunkError: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    // NEVER re-throw here — throwing inside getDerivedStateFromError causes
    // React to unmount the entire tree, leaving a blank screen.
    const chunk = isChunkLoadError(error);
    const msg = error instanceof Error ? error.message : String(error);
    return { hasError: true, isChunkError: chunk, errorMessage: msg };
  }

  componentDidCatch(error: unknown, info: ErrorInfo) {
    if (!this.state.isChunkError) {
      // Non-chunk runtime error — log for debugging, show fallback UI.
      console.error("[ChunkErrorBoundary] Caught runtime error:", error, info);
      return;
    }

    console.warn("[ChunkErrorBoundary] Stale chunk detected — reloading.", error);

    // Guard against infinite reload loops (e.g., server truly missing the file)
    const alreadyAttempted = sessionStorage.getItem(RELOAD_KEY);
    if (alreadyAttempted) {
      console.error("[ChunkErrorBoundary] Reload already attempted, not retrying.", info);
      return;
    }

    sessionStorage.setItem(RELOAD_KEY, "1");
    this.setState({ reloading: true });

    // Hard reload — bypasses browser cache, fetches fresh index.html
    window.location.reload();
  }

  componentDidMount() {
    // Clear the flag on successful mount (fresh page, no error)
    sessionStorage.removeItem(RELOAD_KEY);
  }

  render() {
    if (this.state.reloading) {
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9fafb",
            gap: "16px",
          }}
        >
          <div
            style={{
              width: "40px",
              height: "40px",
              border: "3px solid #2563eb",
              borderTopColor: "transparent",
              borderRadius: "50%",
              animation: "spin 0.8s linear infinite",
            }}
          />
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            Đang tải phiên bản mới nhất...
          </p>
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      );
    }

    if (this.state.hasError) {
      if (!this.state.isChunkError) {
        // Generic runtime error — show error message + reload button
        return (
          <div
            style={{
              position: "fixed",
              inset: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#0f0f0f",
              gap: "12px",
              padding: "24px",
            }}
          >
            <p style={{ color: "#f87171", fontSize: "16px", fontWeight: 600 }}>
              ⚠️ Ứng dụng gặp lỗi không mong muốn
            </p>
            {this.state.errorMessage && (
              <pre
                style={{
                  color: "#9ca3af",
                  fontSize: "12px",
                  maxWidth: "600px",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  background: "#1f1f1f",
                  padding: "12px",
                  borderRadius: "8px",
                }}
              >
                {this.state.errorMessage}
              </pre>
            )}
            <button
              onClick={() => window.location.reload()}
              style={{
                padding: "10px 24px",
                background: "#7c3aed",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: 600,
                cursor: "pointer",
                marginTop: "8px",
              }}
            >
              Tải lại trang
            </button>
          </div>
        );
      }

      // Chunk load error — stale assets, same reload button
      return (
        <div
          style={{
            position: "fixed",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#f9fafb",
            gap: "12px",
          }}
        >
          <p style={{ color: "#374151", fontSize: "16px", fontWeight: 600 }}>
            Ứng dụng đã được cập nhật
          </p>
          <p style={{ color: "#6b7280", fontSize: "14px" }}>
            Vui lòng tải lại trang để tiếp tục.
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "10px 24px",
              background: "#2563eb",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "14px",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Tải lại trang
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
